// import './Interaction.css'
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import logo from '../images/logo.svg';
import axios from '../../helpers/request';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { TimelapseOutlined, Videocam, Login, Visibility } from '@mui/icons-material';

import { DataGridPro } from '@mui/x-data-grid-pro';

import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { useAppState } from '../../state';

const SUPER_ADMIN_ROLE = "super_administrator";
const TRANSCRIPT_ROLE = "transcript";

const MainContainer = styled.main(({ theme }) => ({
  flexGrow: 1,
  minHeight: "100vh",
  overflow: "auto",
  backgroundColor: "#f8fafc",
  padding: 0,
}));

const HeaderBar = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 24px",
  backgroundColor: "#fff",
  borderBottom: "1px solid #e2e8f0",
}));

const HeaderLeft = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const HeaderRight = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const BackButton = styled.span({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  color: "#10416D",
  fontSize: "14px",
  cursor: "pointer",
  "&:hover": {
    color: "#0d3557",
  },
});

const LogoImage = styled.img({
  height: "28px",
});

const HeaderTitle = styled.span({
  fontSize: "13px",
  fontWeight: 500,
  color: "#10416D",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const ContentWrapper = styled.div({
  padding: "24px 32px",
});

const OverviewCard = styled(Paper)({
  backgroundColor: "#fff",
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  padding: "24px",
  marginBottom: "24px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const SectionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#10416D",
  marginBottom: "20px",
});

const DetailsGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "24px",
  marginBottom: "24px",
});

const DetailColumn = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const DetailRow = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const DetailLabel = styled.span({
  fontSize: "11px",
  fontWeight: 500,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const DetailValue = styled.span({
  fontSize: "14px",
  fontWeight: 400,
  color: "#334155",
});

const Badge = styled.span(({ variant }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 10px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 500,
  backgroundColor: variant === "yes" ? "#dcfce7" : "#fee2e2",
  color: variant === "yes" ? "#16a34a" : "#dc2626",
}));

const ButtonGroup = styled.div({
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
});

const PrimaryButton = styled(Button)({
  backgroundColor: "#10416D",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "10px 20px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    backgroundColor: "#10416D",
  },
  "&:disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    color: "#94a3b8",
  },
});

const SecondaryButton = styled(Button)({
  backgroundColor: "#10416D",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "10px 20px",
  borderRadius: "10px",
  border: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    backgroundColor: "#10416D",
  },
  "&:disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    color: "#94a3b8",
  },
});

const ParticipantCard = styled(Paper)({
  backgroundColor: "#fff",
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  '& .MuiDataGrid-root': {
    border: 'none',
    borderRadius: '0px !important',
  },
});

const ParticipantHeader = styled.div({
  backgroundColor: "#f1f5f9",
  padding: "16px 24px",
  borderBottom: "1px solid #e2e8f0",
});

const ParticipantTitle = styled(Typography)({
  fontSize: "15px",
  fontWeight: 600,
  color: "#10416D",
});

const DataGridWrapper = styled.div({
  "& .MuiDataGrid-root": {
    border: "none",
    borderRadius: 0,
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  "& .MuiDataGrid-cell": {
    fontSize: "14px",
    color: "#334155",
    borderBottom: "1px solid #f1f5f9",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f8fafc",
  },
});

const DangetText = styled.span(({ theme }) => ({
  color: theme.palette.error.main
}));

export default function ZoomInteraction() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [meeting, setMeeting] = useState({});
  const { meetingId } = useParams();
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(0);
  const { user } = useAppState();

  useEffect(() => {
    if (meetingId) {
      getMeeting(meetingId);
      getParticipants(meetingId);
    } else {
      navigate('/', { replace: true });
    }
  }, [meetingId, navigate, count])

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(count + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, [count]);

  const getMeeting = (meetingId) => {
    axios.get('/api/meeting/' + meetingId).then(async (response) => {
      if (response && response.status === 200 && response.data) {
        setMeeting(response.data);
      } else {
        throw new Error(response?.error);
      }
    });
  }

  const getParticipants = (meetingId) => {
    let searchParams = {
      search: search
    };
    axios.get('/api/meeting/' + meetingId + '/participant').then(async (response) => {
      if (response && response.status === 200) {
        setData(response.data)
      } else {
        throw new Error(response?.error);
      }
    });
  }


  const downloadRecordingFile = (key, roomid) => {
    axios.get('/internal/recording/download', { params: { key, roomid } }).then(async (response) => {
      if (response && response.status === 200) {
        const link = document.createElement("a");
        link.target = "_blank";
        link.href = response.data.recording_file_url;
        link.click();
      } else {
        alert("Unable to download recording file")
      }
    });
  }

  const downloadTranscriptFile = (key) => {
    axios.get('/api/recording/url', { params: { key } }).then(async (response) => {
      if (response && response.status === 200 && response.data && response.data.transcript_status === "COMPLETED") {
        const link = document.createElement("a");
        link.target = "_blank";
        link.href = response.data.transcript_file_url;
        link.click();
      } else {
        alert("Unable to download recording file")
      }
    });
  }

  const downloadFile = (keyType) => {
    const { meeting_id, room_id } = meeting;

    axios.get('/api/meeting/' + meeting_id + '/recordings').then(async (response) => {
      if (response && response.status === 200 && response.data.length) {
        const { key } = response.data[0];

        if (keyType === "recording") {
          downloadRecordingFile(key, room_id);
        } else if (keyType === "transcript") {
          downloadTranscriptFile(key);
        }
      } else {
        alert("Unable to download recording file")
      }
    }, async (error) => {
      alert(error?.response?.data?.message);
    });
  }

  const columns = [
    {
      field: 'participant_id', headerName: 'Participant ID', flex: 1, sortable: false
    },
    {
      field: 'identity', headerName: 'Participant Display Name', width: 200
    },
    {
      field: 'email', headerName: 'Participant Email', flex: 1
    },
    {
      field: 'join_time', headerName: 'Join time (UTC)', width: 100, sortable: false,
      valueGetter: (params) => params ? moment(params).format('HH:mm') : 'NA'
    },
    {
      field: 'leave_time', headerName: 'Leave time (UTC)', width: 100, sortable: false,
      valueGetter: (params) => params ? moment(params).format('HH:mm') : 'NA'
    },
  ];

  return (
    <MainContainer>
      {/* Header Bar */}
      <HeaderBar>
        <HeaderLeft>
          <Link to={'/zoom'} style={{ textDecoration: "none" }}>
            <BackButton>
              ← Back
            </BackButton>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LogoImage src={logo} alt="Dialectica" />
          </div>
        </HeaderLeft>
        <HeaderRight>
          <HeaderTitle>ZOOM MEETING INTERACTION</HeaderTitle>
        </HeaderRight>
      </HeaderBar>

      <ContentWrapper>
        {/* Interaction Overview Card */}
        <OverviewCard elevation={0}>
          <SectionTitle>Interaction Overview</SectionTitle>

          <DetailsGrid>
            {/* Column 1 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>ZOOM ID</DetailLabel>
                <DetailValue>{meeting.meeting_id || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>TOPIC</DetailLabel>
                <DetailValue>{meeting.topic || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>MAVEN ID</DetailLabel>
                <DetailValue>{meeting.maven_id || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>MAVEN SOURCE</DetailLabel>
                <DetailValue>{meeting.source || "-"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>PARTICIPANTS</DetailLabel>
                <DetailValue>{data.length}</DetailValue>
              </DetailRow>
            </DetailColumn>

            {/* Column 2 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>MEETING DATE</DetailLabel>
                <DetailValue>
                  {meeting.datetime ? moment(meeting.datetime).format("DD/MM/YYYY") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>START TIME (UTC)</DetailLabel>
                <DetailValue>
                  {meeting.started_at ? moment(meeting.started_at).format("HH:mm") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>END TIME (UTC)</DetailLabel>
                <DetailValue>
                  {meeting.ended_at ? moment(meeting.ended_at).format("HH:mm") : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>DURATION</DetailLabel>
                <DetailValue>
                  {meeting.ended_at && meeting.started_at
                    ? meeting.meeting_ended_at
                      ? Math.ceil(
                        moment(meeting.meeting_ended_at).diff(
                          moment(meeting.started_at),
                          "minutes",
                          true
                        )
                      ) + " minute(s)"
                      : Math.ceil(
                        moment(meeting.ended_at).diff(
                          moment(meeting.started_at),
                          "minutes",
                          true
                        )
                      ) + " minute(s)"
                    : "-"}
                </DetailValue>
              </DetailRow>
            </DetailColumn>

            {/* Column 3 */}
            <DetailColumn>
              <DetailRow>
                <DetailLabel>RECORDING</DetailLabel>
                <DetailValue>{meeting.recording ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>ANONYMOUS</DetailLabel>
                <DetailValue>{meeting.anonymous ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>DISCLAIMER</DetailLabel>
                <DetailValue>{meeting.disclaimer ? "Yes" : "No"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>ARCHIVED / DELETED</DetailLabel>
                <DetailValue style={{ display: "flex", gap: "8px" }}>
                  <Badge variant={meeting.is_archived ? "yes" : "no"}>
                    {meeting.is_archived ? "Yes" : "No"}
                  </Badge>
                  <Badge variant={meeting.is_deleted ? "yes" : "no"}>
                    {meeting.is_deleted ? "Yes" : "No"}
                  </Badge>
                </DetailValue>
              </DetailRow>
            </DetailColumn>
          </DetailsGrid>

          {/* Action Buttons */}
          <ButtonGroup>
            {meeting.started_at && !meeting.ended_at ? (
              <SecondaryButton href={meeting.join_link} target="_blank">
                <Login sx={{ fontSize: 18, mr: 1 }} /> Join Meeting
              </SecondaryButton>
            ) : (
              <SecondaryButton disabled={true}>
                <Login sx={{ fontSize: 18, mr: 1 }} /> Join Meeting
              </SecondaryButton>
            )}

            {meeting.started_at && !meeting.ended_at ? (
              <a href={meeting.chaperoning_link} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                <SecondaryButton>
                  <Visibility sx={{ fontSize: 18, mr: 1 }} /> Chaperone Meeting
                </SecondaryButton>
              </a>
            ) : (
              <SecondaryButton disabled={true}>
                <Visibility sx={{ fontSize: 18, mr: 1 }} /> Chaperone Meeting
              </SecondaryButton>
            )}
          </ButtonGroup>
        </OverviewCard>

        {/* Participant Overview Card */}
        <ParticipantCard elevation={0}>
          <ParticipantHeader>
            <ParticipantTitle>Participant Overview</ParticipantTitle>
          </ParticipantHeader>

          <DataGridWrapper>
            <DataGridPro
              rows={data}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              autoHeight={true}
            />
          </DataGridWrapper>

          <div style={{ padding: "8px 16px", fontSize: "13px", color: "#64748b", borderTop: "1px solid #e2e8f0" }}>
            Total Rows: {data.length}
          </div>
        </ParticipantCard>
      </ContentWrapper>
    </MainContainer>
  )
}
